import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import type { BetaAccessStatsResponse } from '@/lib/api';

interface UseBetaAccessStatsReturn {
  stats: BetaAccessStatsResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBetaAccessStats(): UseBetaAccessStatsReturn {
  const [stats, setStats] = useState<BetaAccessStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function fetchStats() {
      setIsLoading(true);
      setError(null);

      try {
        const result = await api.getBetaAccessStats();
        if (!cancelled) {
          setStats(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load stats');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return { stats, isLoading, error, refetch };
}
